import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, MessageSquareIcon } from '@utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import type { ChannelSummary, CommsMessage, MessageSegment } from '@interfaces/ICommsApi';
import { firstUnreadMessageId, repliedPastCursor } from '@d-orca/shared/client/utils';
import type { IPendingAttachment } from '../../hooks/useComposerAttachments';
import { MessageItem } from './MessageItem/MessageItem';
import { useDocumentVisible } from '@hooks/useDocumentVisible';
import { useReadObserver } from './useReadObserver';
import { useUnreadAbove } from './useUnreadAbove';
import { useScrollManager } from './useScrollManager';
import styles from './MessageList.module.css';

interface IMessageListProps {
  channel: ChannelSummary;
  channelName: string; // Display label — channel name, or the DM peer's name
  createdByName: string | undefined; // Name of the channel creator — undefined for DMs and creatorless channels
  messages: CommsMessage[];
  currentUserId: string;
  /**
   * ISO timestamp snapshot of the user's previous read cursor for this
   * channel (captured at channel-open time). A "New messages" divider is
   * rendered above the first incoming message strictly newer than this.
   * Undefined = no divider shown.
   */
  unreadSinceAt: string | undefined;
  /** How many were unread at open — the "unread above" pill's count; 0 shows none. */
  unreadAtOpenCount: number;
  isLoadingHistory: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
  onRetry: (message: CommsMessage) => void;
  onDiscard: (message: CommsMessage) => void;
  /** Files of unconfirmed messages, by clientMessageId. */
  localAttachments: Record<string, IPendingAttachment[] | undefined>;
  onDelete: (message: CommsMessage) => void;
  onEdit: (message: CommsMessage, segments: MessageSegment[]) => Promise<void>;
  onSendDirectMessage: (userId: string) => void;
  /**
   * Messages that were genuinely on screen — half visible for half a second
   * with the tab in front. Evidence for the read record; never the reader's own.
   */
  onRead: (messageIds: string[]) => void;
  onMessageInfo: (message: CommsMessage) => void;
}

function toDateKey(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

const GROUP_GAP_MS = 5 * 60 * 1000; // 5 minutes

function isContinuation(prev: CommsMessage | undefined, curr: CommsMessage): boolean {
  if (!prev) return false;
  if (prev.authorId !== curr.authorId) return false;
  if (toDateKey(prev.createdAt) !== toDateKey(curr.createdAt)) return false;
  return new Date(curr.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_GAP_MS;
}

export function MessageList({
  channel,
  channelName,
  createdByName,
  messages,
  currentUserId,
  unreadSinceAt,
  unreadAtOpenCount,
  isLoadingHistory,
  hasMore,
  isFetchingMore,
  loadMore,
  onRetry,
  onDiscard,
  localAttachments,
  onDelete,
  onEdit,
  onSendDirectMessage,
  onRead,
  onMessageInfo,
}: IMessageListProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  // The "New messages" divider goes above the first incoming message after the read cursor.
  const firstUnreadId = firstUnreadMessageId(messages, currentUserId, unreadSinceAt);

  const { scrollViewportRef, scrollContentRef, loadOlderTriggerRef, isAtBottom, newMessageCount, scrollToBottom } =
    useScrollManager({ messages, currentUserId, hasMore, loadMore });

  const visible = useDocumentVisible();
  // The viewport element exists only after the first commit; hold it in state
  // so the observer is created once it does, rather than reading a null ref.
  const [viewportEl, setViewportEl] = useState<HTMLElement | null>(null);
  useEffect(() => { setViewportEl(scrollViewportRef.current); }, [scrollViewportRef, messages.length]);
  const registerReadRow = useReadObserver({ root: viewportEl, enabled: visible, onRead });
  const unreadAbove = useUnreadAbove({
    root: viewportEl,
    unreadAtOpenCount,
    hasCursor: unreadSinceAt !== undefined,
    repliedPast: repliedPastCursor(messages, currentUserId, unreadSinceAt),
  });

  const [pendingDelete, setPendingDelete] = useState<CommsMessage | null>(null);

  function handleDeleteConfirm() {
    if (pendingDelete) onDelete(pendingDelete);
    setPendingDelete(null);
  }

  const showLoading = isLoadingHistory && messages.length === 0;
  const showEmpty = !isLoadingHistory && messages.length === 0;

  return (
    <>
      <ScrollArea className={styles.root} viewportClassName={styles.viewport} viewportRef={scrollViewportRef}>
        {showLoading && (
          <div className={styles.empty}>
            <div className={styles.emptyHint}>{t('comms.messages.loadingHistory')}</div>
          </div>
        )}

        {showEmpty && (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}><MessageSquareIcon size={40} /></span>
            <div className={styles.emptyTitle}>
              {channel.kind === 'dm'
                ? t('comms.messages.emptyTitleDm', { name: channelName })
                : t('comms.messages.emptyTitle', { channel: channelName })}
            </div>
            <div className={styles.emptyMeta}>
              {channel.kind !== 'dm' && t('comms.header.members', { count: channel.memberCount })}
              {channel.kind !== 'dm' && channel.createdAt && (
                <> · {createdByName
                  ? t('comms.messages.emptyCreatedBy', { date: dateFmt.format(new Date(channel.createdAt)), name: createdByName })
                  : t('comms.messages.emptyCreatedAt', { date: dateFmt.format(new Date(channel.createdAt)) })}</>
              )}
            </div>
            <div className={styles.emptyHint}>{t('comms.messages.emptyHint')}</div>
          </div>
        )}

        {unreadAbove.show && (
          <button
            type="button"
            className={styles.unreadAboveBtn}
            onClick={unreadAbove.jumpToUnread}
            data-testid="unread-above"
          >
            <span className={styles.chevronUp}><ChevronDownIcon size={14} /></span>
            {t('comms.messages.unreadAbove', { count: unreadAtOpenCount })}
          </button>
        )}

        {messages.length > 0 && (
          <div ref={scrollContentRef} className={styles.list}>
            <div ref={loadOlderTriggerRef} />
            {isFetchingMore && (
              <div className={styles.loadingMore}>{t('comms.messages.loadingMore')}</div>
            )}
            {messages.map((message, index) => {
              const prev = messages[index - 1];
              const showDateSep = !prev || toDateKey(prev.createdAt) !== toDateKey(message.createdAt);
              const grouped = !showDateSep && isContinuation(prev, message);
              const showUnreadDivider = message.id === firstUnreadId;
              const theirs = message.authorId !== currentUserId;
              return (
                <div
                  key={message.id}
                  data-message-id={theirs ? message.id : undefined}
                  ref={theirs ? registerReadRow : undefined}
                >
                  {showDateSep && (
                    <div className={styles.dateSeparator}>
                      <span className={styles.dateSeparatorLabel}>
                        {dateFmt.format(new Date(message.createdAt))}
                      </span>
                    </div>
                  )}
                  {showUnreadDivider && (
                    <div className={styles.unreadDivider} ref={unreadAbove.registerDivider}>
                      <span className={styles.unreadDividerLabel}>
                        {t('comms.messages.newMessagesDivider')}
                      </span>
                    </div>
                  )}
                  <MessageItem
                    message={message}
                    isOwn={message.authorId === currentUserId}
                    grouped={grouped}
                    onRetry={onRetry}
                    onDiscard={onDiscard}
                    localAttachments={message.clientMessageId ? localAttachments[message.clientMessageId] : undefined}
                    onDelete={message.authorId === currentUserId ? () => setPendingDelete(message) : undefined}
                    onEdit={message.authorId === currentUserId ? (segments) => onEdit(message, segments) : undefined}
                    onSendDirectMessage={message.authorId === currentUserId || channel.kind === 'dm' ? undefined : () => onSendDirectMessage(message.authorId)}
                    onInfo={message.authorId === currentUserId && !message.status ? () => onMessageInfo(message) : undefined}
                  />
                </div>
              );
            })}
          </div>
        )}

        {!isAtBottom && (
          <button
            type="button"
            className={styles.scrollToBottomBtn}
            onClick={scrollToBottom}
            aria-label={t('comms.messages.scrollToBottom')}
          >
            <ChevronDownIcon size={16} />
            {newMessageCount > 0 && (
              <span className={styles.newMessageBadge}>{newMessageCount}</span>
            )}
          </button>
        )}
      </ScrollArea>

      <DiscardModal
        open={pendingDelete !== null}
        title={t('comms.messages.deleteConfirm')}
        confirmLabel={t('comms.deleteChannel.yes')}
        cancelLabel={t('comms.deleteChannel.no')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
